import type { DefaultOptions } from 'react-query';
import { dehydrate, QueryClient } from 'react-query';
import type { U } from 'ts-toolbelt';

import { HttpError } from '~/api/client';
import { fetchOAIData } from '~/libs/openapi';
import type { OAIPathKeysByMethod } from '~/types/openapi';

import { OAIApiError } from './openApiError';

export const reactQueryDefaultOptions: DefaultOptions['queries'] = {
  suspense: true,
  notifyOnChangeProps: 'tracked',
  staleTime: 10 * 1000,
  // ErrorBoundary를 사용할 경우에만 직접 useErrorBoundary를 세팅해서 사용
  useErrorBoundary(error) {
    // OAIApiError (기존 API 레이어)
    if (error instanceof OAIApiError) {
      // c1(403), 미로그인(401)유저는 마켓 접근 불가 -> 에러 페이지로 던짐
      return error.statusCode === 401 || error.statusCode === 403;
    }
    // HttpError (신규 API 레이어)
    if (error instanceof HttpError) {
      return error.status === 401 || error.status === 403;
    }
    return false;
  },
  // retry를 해도 성공하는 경우가 극소수일 것으로 retry하지 않는다
  retry: false,
};

export const createReactQueryClient = (options?: DefaultOptions) => {
  return new QueryClient({
    defaultOptions: {
      ...options,
      queries: {
        ...reactQueryDefaultOptions,
        // server에서는 suspense가 걸릴 수 있어야 hydration error를 피할 수 있기 떄문에 server에서는 실행시킨다.
        ...options?.queries,
      },
    },
  });
};

type PrefetchKey =
  | OAIPathKeysByMethod<'get'>
  | [OAIPathKeysByMethod<'get'>, Record<string, unknown>];

type PrefetchParams = U.Strict<
  | {
      queries: PrefetchKey[];
    }
  | {
      infiniteQueries: PrefetchKey[];
    }
  | {
      queries: PrefetchKey[];
      infiniteQueries: PrefetchKey[];
    }
> & {
  queryClient?: QueryClient;
  fetchOptions?: RequestInit;
};

const createPrefetchQueryKey = (prefetchKey: PrefetchKey) => {
  const [queryKey, variables] = Array.isArray(prefetchKey)
    ? prefetchKey
    : [prefetchKey];
  return variables ? [queryKey, variables] : queryKey;
};

const createSendPrefetchRequest = (
  prefetchKey: PrefetchKey,
  fetchOptions?: RequestInit,
) => {
  return async () => {
    const [queryKey, variables] = Array.isArray(prefetchKey)
      ? prefetchKey
      : [prefetchKey];

    const data = await fetchOAIData({
      queryKey,
      variables,
      fetchOptions,
    });

    return data;
  };
};

// dehydrate 되는 시간동안 stale 상태가되면 CSR 단계에서 네트워크 재호출하는것 방지
const prefetchDefaultOption = {
  staleTime: 5000,
  cacheTime: 5000 * 2,
};

export const prefetchReactQueryData = async ({
  queries,
  infiniteQueries,
  fetchOptions,
  queryClient = createReactQueryClient({
    queries: {
      ...prefetchDefaultOption,
    },
  }),
}: PrefetchParams) => {
  await Promise.all(
    queries?.map((prefetchKey) =>
      queryClient.prefetchQuery(
        createPrefetchQueryKey(prefetchKey),
        createSendPrefetchRequest(prefetchKey, fetchOptions),
      ),
    ) || [],
  );
  await Promise.all(
    infiniteQueries?.map((prefetchKey) =>
      queryClient.prefetchInfiniteQuery(
        createPrefetchQueryKey(prefetchKey),
        createSendPrefetchRequest(prefetchKey, fetchOptions),
      ),
    ) || [],
  );

  return {
    queryClient,
    // https://github.com/tannerlinsley/react-query/issues/1458#issuecomment-747716357
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    dehydratedState: JSON.parse(
      JSON.stringify(dehydrate(queryClient)),
    ) as Record<string, unknown>,
  };
};
